import { useEffect, useRef, useState } from "react";
import { Compass, Target, Map, BookOpen, Lightbulb, ChevronDown, Shield, Sparkles, Printer, Bookmark, BookmarkCheck, ArrowRight, Briefcase, Rocket, GraduationCap, TrendingUp, TrendingDown, Minus, Shuffle, CheckCircle2, Layers, X, MapPin, Flag, Activity } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import client from "../lib/api";
import { fireConfetti } from "../lib/confetti";
import { ArchetypeGlyph } from "../components/ArchetypeGlyph";
import { RadarChart, SignalLegend, LAYER_ORDER, matchLayer, layerScore } from "../components/RadarChart";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import iconWhite from "../assets/brand/klareet-icon-white.png";
import { CareerFit, InterestCompass, BarList, GoalReadinessGauge, StrengthsWatchouts, BehaviouralStudy, DrivingGoalBar } from "../components/insight/InsightKit";
import { PulseTrend } from "../components/WeeklyPulse";
import { ArchetypeEmblem, ResemblanceCard, ShareArchetypeCard } from "../components/ArchetypeIdentityKit";

/**
 * Your read.
 *
 * Launch mode: anyone with a login sees the full read. Every non
 * sealed section is open by default. The derailer section stays
 * permanently sealed on this surface (SI-15): it is an individual
 * only, development only signal and never opens through any gate on
 * this screen.
 *
 * Institutional purchases still happen through the seats service
 * offline; this surface never renders a price and never links to
 * checkout.
 */

const B2_SECTIONS = [
  {
    key: "interests", title: "What pulls you",
    subtitle: "the shape of what you keep coming back to",
    layer: "interests", hue: "L4",
  },
  {
    key: "traits", title: "How you show up",
    subtitle: "how you tend to act day to day",
    layer: "traits", hue: "L1",
  },
  {
    key: "aptitudes", title: "Where thinking lands",
    subtitle: "how your reasoning tends to move",
    layer: "aptitudes", hue: "L2",
  },
  {
    key: "values", title: "What you protect",
    subtitle: "the ground you will not give up",
    layer: "values", hue: "L5",
  },
  {
    key: "motivators", title: "What keeps you moving",
    subtitle: "what pulls the effort out of you",
    layer: "motivators", hue: "L6",
  },
  {
    key: "situation", title: "Where you are right now",
    subtitle: "what you told us about right now",
    layer: "situation", hue: "L3",
  },
  {
    key: "readiness", title: "Ready to move",
    subtitle: "what helps you see a plan through",
    layer: "readiness", hue: "L7",
  },
  // Derailer sits under a permanent seal on this surface.
  {
    key: "derailer", title: "Your watch-outs",
    subtitle: "kept private, just for you",
    layer: null, hue: "L1", sealed: true,
  },
];

export default function ReportScreen({ product }) {
  const [report, setReport] = useState(null);
  const [insight, setInsight] = useState(null);
  const [openSection, setOpenSection] = useState(null);
  const [intake, setIntake] = useState(null);
  const [savedTitles, setSavedTitles] = useState(() => new Set());
  const [name, setName] = useState("");
  const [reveal, setReveal] = useState(false);
  const [revealClosing, setRevealClosing] = useState(false);
  const heroRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const timers = [];
    const playReveal = (rep) => {
      const seen = localStorage.getItem("klareet.report.seen");
      if (!seen && rep) {
        setReveal(true);
        timers.push(setTimeout(() => setRevealClosing(true), 2300));
        timers.push(setTimeout(() => {
          setReveal(false);
          if (heroRef.current) fireConfetti(heroRef.current);
          localStorage.setItem("klareet.report.seen", "1");
        }, 2750));
      }
    };

    const raw = localStorage.getItem("klareet.report");
    let rep = null;
    if (raw) {
      try { rep = JSON.parse(raw); setReport(rep); } catch (_e) { /* ignore */ }
    }
    if (rep) {
      playReveal(rep);
    } else {
      // No cached read (for example a fresh sign in on a new device).
      // Pull the current read from the backend and prime the cache so
      // the Snapshot loads even when nothing is stored locally.
      client.get(`/demo/read/latest?product=${product}`)
        .then((r) => {
          const fetched = r?.data?.report;
          if (fetched) {
            setReport(fetched);
            try { localStorage.setItem("klareet.report", JSON.stringify(fetched)); } catch (_e) { /* ignore */ }
            playReveal(fetched);
          }
        })
        .catch(() => { /* no read yet */ });
    }
    client.get("/consent/status")
      .then((r) => { const f = (r.data?.display_name || "").trim(); if (f) setName(f.split(/\s+/)[0]); })
      .catch(() => {});
    client.get(`/demo/read/intake?product=${product}`)
      .then((r) => setIntake(r?.data || null))
      .catch(() => setIntake(null));
    client.get(`/demo/insight?product=${product}`)
      .then((r) => setInsight(r?.data || null))
      .catch(() => setInsight(null));
    // Load which titles the person has already saved to their
    // reading rail, so the Save button flips to "Saved" on load.
    client.get("/reading-rail")
      .then((r) => {
        const titles = new Set((r?.data?.entries || []).map((e) => e.title));
        setSavedTitles(titles);
      })
      .catch(() => { /* rail is a soft add; leave the set empty */ });
    return () => timers.forEach(clearTimeout);
  }, [product]);

  const saveBook = async (book) => {
    if (savedTitles.has(book.title)) return;
    try {
      await client.post("/reading-rail", {
        title: book.title, author: book.author,
        why: book.why || "", anchor: book.anchor || "",
        cover_url: book.cover_url || "",
      });
      setSavedTitles((prev) => {
        const next = new Set(prev);
        next.add(book.title);
        return next;
      });
    } catch (_e) {
      // Silent: the rail is soft ux. A retry on next page load will
      // still land through the idempotent POST.
    }
  };

  const toggleSection = (key) => {
    setOpenSection((cur) => (cur === key ? null : key));
  };

  // KLA-040: quick view or the full read. Sticky per device, quick
  // first so the snapshot lands readable, the depth one tap away.
  const [depth, setDepth] = useState(() => {
    try { return localStorage.getItem("klareet.report.depth") || "quick"; } catch (_e) { return "quick"; }
  });
  const setDepthSticky = (d) => {
    setDepth(d);
    try { localStorage.setItem("klareet.report.depth", d); } catch (_e) { /* ignore */ }
  };

  if (!report) {
    return (
      <div className="max-w-2xl mx-auto px-4 pt-20 text-center">
        <p className="k-quiet">No read on this device yet. Complete a conversation first.</p>
      </div>
    );
  }

  const A = report.archetype;
  const goal = report.goal_model;

  return (
    <div className="relative max-w-5xl mx-auto px-5 pt-8 pb-24">
      {reveal && (
        <div
          className={`k-reveal-overlay${revealClosing ? " closing" : ""}`}
          data-testid="report-reveal"
          aria-hidden="true"
        >
          <div className="k-reveal-inner">
            <span className="k-chip k-chip-teal mx-auto mb-4">here's your read</span>
            <div className="k-reveal-lead">
              Okay{name ? `, ${name}` : ""}. Here is what I found.
            </div>
            <div className="k-reveal-arch">{A?.primary?.name}</div>
            {A?.secondary?.name && (
              <div className="k-reveal-arch-sub">
                with a {A.secondary.name.replace(/^the\s+/i, "")} streak
              </div>
            )}
          </div>
        </div>
      )}
      <div className="k-orb k-orb-blue"  style={{ top: "-8rem", left: "-6rem", width: "24rem", height: "24rem" }} />
      <div className="k-orb k-orb-teal"  style={{ top: "18rem", right: "-8rem", width: "20rem", height: "20rem" }} />
      <div className="k-orb k-orb-amber" style={{ bottom: "-6rem", left: "20%", width: "18rem", height: "18rem" }} />

      {/* Print action. Fixed on-screen, hidden on paper. The full
          page prints minus sidebar/header via the global print
          stylesheet, and a person can share the print in a career
          office the same way admins print the cohort view. */}
      <div className="flex justify-end mb-3 print:hidden">
        <button
          type="button"
          onClick={() => window.print()}
          className="k-btn-outline"
          data-testid="report-print-btn"
          aria-label="Print this read"
        >
          <Printer size={14} /> Print this read
        </button>
      </div>

      {/* Immersive archetype hero */}
      <div
        ref={heroRef}
        className="relative overflow-hidden rounded-[28px] p-8 sm:p-10 k-fade-in mb-8"
        style={{
          background: "linear-gradient(135deg, #16193A 0%, #2B2150 52%, #45184F 100%)",
          color: "white",
          boxShadow: "0 40px 80px -40px rgba(60,27,78,0.7), inset 0 1px 0 rgba(255,255,255,0.08)",
        }}
      >
        <div className="k-hero-grid" />
        <div className="k-hero-sheen" />
        <div className="k-orb k-orb-drift k-orb-teal"   style={{ top: "-5rem", left: "38%", width: "22rem", height: "22rem", opacity: 0.4 }} />
        <div className="k-orb k-orb-drift k-orb-drift-2 k-orb-amber" style={{ bottom: "-5rem", right: "-3rem", width: "17rem", height: "17rem", opacity: 0.28 }} />
        <div className="k-orb k-orb-drift k-orb-drift-3 k-orb-blue"  style={{ top: "30%", left: "-4rem", width: "16rem", height: "16rem", opacity: 0.3 }} />
        {/* KLA-040: the archetype's own living mark. */}
        <div className="absolute top-8 right-8 hidden md:block">
          <ArchetypeEmblem arch={A?.primary} size={132} />
        </div>
        <div className="relative">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-[13px] flex items-center justify-center" style={{ background: "rgba(13,17,25,0.85)", border: "1px solid rgba(255,255,255,0.12)", boxShadow: "0 6px 18px -6px rgba(0,0,0,0.6)" }}>
              <img src={iconWhite} alt="" aria-hidden="true" className="w-6 h-6" />
            </div>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.18)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", color: "rgba(255,255,255,0.85)", letterSpacing: "0.12em", textTransform: "uppercase" }}>
              <Sparkles size={11} /> Your archetype
            </span>
          </div>
          <h1
            className="mb-4"
            style={{
              fontFamily: "Source Serif 4, Georgia, serif",
              fontSize: "clamp(2.25rem, 5.5vw, 3.6rem)",
              lineHeight: 1.05,
              letterSpacing: "-0.02em",
              fontWeight: 600,
              color: "white",
            }}
            data-testid="report-title"
          >
            {A?.primary?.name}
            <br />
            <span
              style={{
                background: "linear-gradient(135deg, #FCE7DD, #E0457B 40%, #6D4AE0 90%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              + {A?.secondary?.name}
            </span>
          </h1>
          <p
            style={{
              maxWidth: "36rem",
              fontFamily: "Source Serif 4, Georgia, serif",
              fontSize: "1.1rem",
              lineHeight: 1.55,
              color: "rgba(255,255,255,0.85)",
            }}
            data-testid="report-honest-line"
          >
            {report.honest_line}
          </p>
        </div>
      </div>

      <Tabs defaultValue="my-read" className="w-full">
        <div className="flex justify-center sm:justify-start mb-8 overflow-x-auto print:hidden">
          <TabsList className="k-tablist">
            <TabsTrigger className="k-tab" value="my-read" data-testid="tab-my-read">My Read</TabsTrigger>
            <TabsTrigger className="k-tab" value="career-fit" data-testid="tab-career-fit">Career Fit</TabsTrigger>
            <TabsTrigger className="k-tab" value="the-path" data-testid="tab-the-path">The Path</TabsTrigger>
            <TabsTrigger className="k-tab" value="library" data-testid="tab-library">Library</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="my-read" data-testid="panel-my-read">
      <DrivingGoalBar
        base={product === "grow" ? "/grow" : ""}
        onChange={() => client.get(`/demo/insight?product=${product}`).then((r) => setInsight(r?.data || null)).catch(() => {})}
      />
      {insight?.goal_line && (
        <p className="k-hero-sub mb-6" data-testid="insight-goal-line">{insight.goal_line}</p>
      )}
      {/* KLA-040: quick view or the full read. */}
      <div className="flex items-center justify-end gap-2 mb-5 print:hidden" data-testid="report-depth-toggle">
        <button onClick={() => setDepthSticky("quick")} className={`k-btn !py-1.5 !px-3 !text-xs ${depth === "quick" ? "k-btn-primary" : "k-btn-ghost"}`} data-testid="depth-quick">Quick view</button>
        <button onClick={() => setDepthSticky("full")} className={`k-btn !py-1.5 !px-3 !text-xs ${depth === "full" ? "k-btn-primary" : "k-btn-ghost"}`} data-testid="depth-full">Full read</button>
      </div>

      <TopSummaryCard report={report} />
      <ShareArchetypeCard report={report} personName={name} />
      {depth === "full" && (<>
      <SinceLastCheckin product={product} />
      <SnapshotPulse product={product} />
      </>)}
      {/* Archetype cards */}
      <div className="grid sm:grid-cols-2 gap-5 mb-8">
        <ArchCard label="Primary" arch={A?.primary} tone="violet" testid="archetype-primary" delay={220} />
        <ArchCard label="Secondary" arch={A?.secondary} tone="blue" testid="archetype-secondary" delay={300} />
      </div>

      <ResemblanceCard arch={A?.primary} />

      {depth === "quick" && (
        <button
          onClick={() => setDepthSticky("full")}
          className="k-btn-outline w-full justify-center !py-3 mb-8 flex items-center gap-2"
          data-testid="depth-open-full"
        >
          <ChevronDown size={15} /> See my full read, all seven parts
        </button>
      )}

      {depth === "full" && (<>
      {/* Six-dimension goal model */}
      {goal && <GoalModelCard goal={goal} />}
      {goal?.next_move && <NextMoveCallout nextMove={goal.next_move} />}
      {Array.isArray(report.predictions) && report.predictions.length > 0 && (
        <PredictionsCard predictions={report.predictions} />
      )}

      {/* KLA-005: the person's own questions, answered from the read. */}
      {Array.isArray(report.your_questions) && report.your_questions.length > 0 && (
        <div className="k-accent-card k-acc-violet mb-8 k-fade-in" style={{ animationDelay: "340ms" }} data-testid="your-questions-card">
          <div className="flex items-center gap-2.5 mb-3">
            <span className="k-icon-tile k-icon-tile-sm" aria-hidden="true"><Lightbulb size={16} /></span>
            <div className="k-acc-label">your questions, answered</div>
          </div>
          <p className="k-quiet !text-sm mb-4">You asked these along the way. Here is what your read says about each one, honestly.</p>
          <div className="space-y-4">
            {report.your_questions.map((qa, i) => (
              <div key={i} data-testid={`your-question-${i + 1}`}>
                <p className="klareet-serif mb-1" style={{ fontSize: "1.05rem", color: "hsl(var(--klareet-ink))", fontWeight: 600 }}>
                  &ldquo;{qa.question}&rdquo;
                </p>
                <p className="text-sm" style={{ color: "hsl(var(--klareet-ink-soft))", lineHeight: 1.6 }}>{qa.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Radar and legend */}
      <div className="k-accent-card k-acc-teal mb-8 k-fade-in" style={{ animationDelay: "360ms" }} data-testid="radar-card">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div className="flex justify-center">
            <RadarChart layers={report.layers} size={340} />
          </div>
          <div>
            <div className="flex items-center gap-2.5 mb-3">
              <span className="k-icon-tile k-icon-tile-sm" aria-hidden="true"><Compass size={16} /></span>
              <div className="k-acc-label">your six sides</div>
            </div>
            <p className="klareet-serif mb-4" style={{ fontSize: "1.15rem", color: "hsl(var(--klareet-ink))", lineHeight: 1.5 }}>
              Six points, one for each side of you. Each point is the average of the strongest quotes from your own answers, on a 0.0 to 5.0 scale. A point sitting in the centre just means we did not hear enough on it yet, not that you scored zero.
            </p>
            <SignalLegend layers={report.layers} />
          </div>
        </div>
      </div>

      {/* Full profile. Seven scored sections plus a sealed derailer. */}
      <div className="k-accent-card k-acc-blue mb-8 k-fade-in" style={{ animationDelay: "420ms" }} data-testid="b2-full-profile">
        <div className="flex items-center justify-between mb-3 gap-3">
          <div className="flex items-center gap-3">
            <span className="k-icon-tile" aria-hidden="true"><Sparkles size={20} /></span>
            <div>
              <div className="k-acc-label">your full read</div>
              <div className="klareet-serif" style={{ fontSize: "1.35rem" }}>Seven parts open. One kept private.</div>
            </div>
          </div>
          <div className="k-chip" data-testid="tier-badge">
            <Sparkles size={12} /> full read
          </div>
        </div>

        {/* Explainer. What the number is, what "no signal yet" means. */}
        <div
          className="rounded-xl p-3 mb-4 flex items-start gap-3"
          style={{
            background: "hsl(var(--klareet-canvas-2))",
            border: "1px solid hsl(var(--klareet-line-soft))",
          }}
          data-testid="b2-explainer"
        >
          <Lightbulb size={16} style={{ color: "hsl(var(--klareet-blue))", marginTop: 2 }} />
          <div className="k-quiet !text-sm" style={{ lineHeight: 1.6, color: "hsl(var(--klareet-ink))" }}>
            <div className="font-semibold mb-1" style={{ color: "hsl(var(--klareet-ink))" }}>About these numbers</div>
            Each part shows an average from 0.0 to 5.0, based on the strongest quotes from your own answers.
            A part marked <em>nothing yet</em> just means we did not hear enough to score it,
            not that you scored zero. This is an early read, so the more you talk, the more it can move.
            The private part is a growth note kept only for you.
          </div>
        </div>

        <div className="space-y-2" data-testid="b2-sections">
          {B2_SECTIONS.map((s) => {
            const layer = s.layer ? report.layers.find((l) => matchLayer(l.layer)?.key === s.layer) : null;
            const rawScore = s.layer ? layerScore(layer) : null;
            const constructCount = layer?.top_constructs?.length || 0;
            // For "situation", we do NOT render a score chip; we
            // render the intake context body. For every other
            // section, empty top_constructs means "no signal yet".
            const isSituation = s.key === "situation";
            const situationHasContent = isSituation && intake?.has_intake;
            const hasScore = !isSituation && rawScore != null && !s.sealed;
            const open = openSection === s.key;
            const sealed = !!s.sealed;
            const testKey = `section-${s.key}`;
            return (
              <div
                key={s.key}
                className="rounded-xl overflow-hidden"
                style={{
                  border: "1px solid hsl(var(--klareet-line-soft))",
                  background: `linear-gradient(180deg, hsl(var(--k-${s.hue}) / 0.05), hsl(var(--card)))`,
                }}
                data-testid={testKey}
              >
                <button
                  onClick={() => toggleSection(s.key)}
                  className="w-full flex items-center gap-3 p-4 text-left"
                  data-testid={`${testKey}-toggle`}
                  aria-expanded={open}
                  aria-label={`${s.title}, ${open ? "collapse" : "expand"}`}
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ background: `hsl(var(--k-${s.hue}))` }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold" style={{ color: "hsl(var(--klareet-ink))" }}>{s.title}</div>
                    <div className="k-quiet !text-xs">
                      {sealed ? "kept private, just for you" : s.subtitle}
                    </div>
                  </div>
                  {!sealed && hasScore && (
                    <div className="flex flex-col items-end shrink-0" data-testid={`${testKey}-cue`}>
                      <span
                        className="klareet-mono text-sm"
                        style={{ color: `hsl(var(--k-${s.hue}))` }}
                        data-testid={`${testKey}-score`}
                      >
                        {rawScore.toFixed(2)}
                      </span>
                      {constructCount > 0 && (
                        <span className="klareet-mono !text-[10px] k-quiet" data-testid={`${testKey}-cited`}>
                          {constructCount} {constructCount === 1 ? "quote from you" : "quotes from you"}
                        </span>
                      )}
                    </div>
                  )}
                  {isSituation && situationHasContent && (
                    <span
                      className="klareet-mono text-xs px-2 py-0.5 rounded-full"
                      style={{
                        color: "hsl(var(--klareet-muted))",
                        background: "hsl(var(--klareet-canvas-2))",
                        border: "1px solid hsl(var(--klareet-line-soft))",
                      }}
                      data-testid={`${testKey}-count`}
                    >
                      {intake.items.length} {intake.items.length === 1 ? "note" : "notes"}
                    </span>
                  )}
                  {!sealed && !hasScore && !situationHasContent && !isSituation && (
                    <span
                      className="klareet-mono text-xs px-2 py-0.5 rounded-full"
                      style={{
                        color: "hsl(var(--klareet-muted))",
                        background: "hsl(var(--klareet-canvas-2))",
                        border: "1px solid hsl(var(--klareet-line-soft))",
                      }}
                      data-testid={`${testKey}-nosignal`}
                    >
                      nothing yet
                    </span>
                  )}
                  {isSituation && !situationHasContent && (
                    <span
                      className="klareet-mono text-xs px-2 py-0.5 rounded-full"
                      style={{
                        color: "hsl(var(--klareet-muted))",
                        background: "hsl(var(--klareet-canvas-2))",
                        border: "1px solid hsl(var(--klareet-line-soft))",
                      }}
                      data-testid={`${testKey}-nosignal`}
                    >
                      nothing yet
                    </span>
                  )}
                  {sealed && <Shield size={14} style={{ color: "hsl(var(--klareet-ink-soft))" }} />}
                  <ChevronDown size={16} style={{
                    transform: open ? "rotate(180deg)" : "none",
                    transition: "transform 0.2s ease",
                    color: "hsl(var(--klareet-muted))",
                  }} />
                </button>

                {open && (
                  <div className="px-4 pb-4 k-fade-in" data-testid={`${testKey}-body`}>
                    {sealed
                      ? <DerailerSealBody />
                      : isSituation
                        ? <SituationBody intake={intake} hue={s.hue} />
                        : hasScore
                          ? <SectionBody layer={layer} hue={s.hue} />
                          : <NoSignalBody hue={s.hue} subtitle={s.subtitle} />}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

          {insight && (
            <div className="mt-8 space-y-6" data-testid="insight-study">
              <BehaviouralStudy dimensions={insight.dimensions || []} />
              <div className="grid md:grid-cols-2 gap-5">
                <InterestCompass data={insight.interest_compass || []} />
                <BarList title="how you show up" sub="The way people experience you in a room." icon={<Layers size={14} />} data={insight.behaviour_profile || []} hue="258 60% 55%" testid="insight-behaviour" />
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <BarList title="where thinking lands" sub="How your mind prefers to solve a hard problem." icon={<Compass size={14} />} data={insight.aptitudes || []} hue="219 76% 47%" testid="insight-aptitudes" />
                <BarList title="what keeps you moving" sub="The drives underneath the goal." icon={<Sparkles size={14} />} data={insight.drives || []} hue="32 80% 45%" testid="insight-drives" />
              </div>
              <GoalReadinessGauge data={insight.goal_readiness} />
              <StrengthsWatchouts data={insight.strengths_watchouts} />
            </div>
          )}
      </>)}
        </TabsContent>

        <TabsContent value="career-fit" data-testid="panel-career-fit">
          <CareerFit insight={insight} goalTitle={insight?.driving_goal?.title} product={product} />
        </TabsContent>

        <TabsContent value="the-path" data-testid="panel-the-path">
          <PathPanel
            report={report}
            goal={goal}
            onGoRoadmap={() => navigate(product === "grow" ? "/grow/roadmap" : "/roadmap")}
          />
        </TabsContent>

        <TabsContent value="library" data-testid="panel-library">
          <LibraryPanel report={report} savedTitles={savedTitles} onSaveBook={saveBook} />
        </TabsContent>
      </Tabs>

      <p className="k-quiet mt-8">
        Your archetype, your read, and your learning list are all yours to keep.
      </p>
    </div>
  );
}

// -----------------------------------------------------------------

function GoalModelCard({ goal }) {
  const rows = [
    { key: "stated",     label: "What you said",              value: goal.stated },
    { key: "revealed",   label: "What the answers showed",    value: goal.revealed },
    { key: "ownership",  label: "Whose goal it is",           value: goal.ownership },
    { key: "direction",  label: "Toward, or away from",       value: goal.direction },
    { key: "reachable",  label: "How reachable it is",        value: goal.reachable },
    { key: "underneath", label: "The question underneath",    value: goal.real_question },
  ];
  return (
    <div className="k-accent-card k-acc-amber k-fade-in mb-8" style={{ animationDelay: "320ms" }} data-testid="goal-model-card">
      <div className="flex items-center gap-3 mb-4">
        <span className="k-icon-tile" aria-hidden="true"><Compass size={20} /></span>
        <div>
          <div className="k-acc-label">your goal, six ways to see it</div>
          <p className="klareet-serif" style={{ fontSize: "1.1rem", color: "hsl(var(--klareet-ink))", lineHeight: 1.4 }}>
            A goal is not a single line. Here is how yours reads, from what you said all the way to the question underneath it.
          </p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3" data-testid="goal-model-rows">
        {rows.map((r) => (
          <div
            key={r.key}
            className="k-tint p-4"
            data-testid={`goal-model-${r.key}`}
          >
            <div className="k-acc-label mb-1">{r.label}</div>
            <div style={{ color: "hsl(var(--klareet-ink))", fontSize: "0.95rem", lineHeight: 1.4 }}>
              {r.value || "not enough yet"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NextMoveCallout({ nextMove }) {
  return (
    <div
      className="k-accent-card k-acc-teal k-fade-in mb-8 flex items-start gap-4"
      style={{ animationDelay: "360ms" }}
      data-testid="next-move-callout"
    >
      <span className="k-icon-tile" aria-hidden="true">
        <Target size={20} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="k-acc-label mb-1">your next move</div>
        <p
          className="klareet-serif"
          style={{
            color: "hsl(var(--klareet-ink))",
            fontSize: "1.15rem",
            lineHeight: 1.55,
          }}
          data-testid="next-move-text"
        >
          {nextMove}
        </p>
        <div className="k-quiet mt-2" style={{ fontSize: "0.8rem" }}>
          A growth step, just for you. Not a score, not a ranking.
        </div>
      </div>
    </div>
  );
}

const MV_LAYER_LABEL = {
  interests: "interests", traits: "traits", aptitudes: "aptitudes",
  values: "values", motivators: "drive", cognitive: "thinking",
  emotional: "steadiness", readiness: "readiness",
  competencies: "skills", personality: "traits",
};
function plainLayer(k) {
  return MV_LAYER_LABEL[k] || (k || "").replace(/_/g, " ");
}

/**
 * A compact "since your last check in" strip for the My Read tab. Reads
 * the same movement source Home uses. On a first read it invites a check
 * in; once there are two or more reads it shows the biggest mover and a
 * per-layer delta row. Movement is a person's own line, never a rank.
 */
function pulseMovement(history) {
  if (!history.length) return "";
  const avg = (h) => (((h.q1 ?? 3) + (h.q2 ?? 3)) / 2);
  if (history.length === 1) return "You logged your first weekly pulse. A few more weeks and this line starts to tell a story.";
  const first = avg(history[0]);
  const last = avg(history[history.length - 1]);
  if (last > first + 0.4) return "Your weekly reads have been climbing. Whatever you are doing, it is working.";
  if (last < first - 0.4) return "Your weekly reads dipped a little lately. That is worth a gentle look, not a worry.";
  return "Your weekly reads have held pretty steady, and steady is underrated.";
}

/** The weekly pulse, folded into the Snapshot so the read visibly moves
 *  with every week the owner logs. Reads the same owner-only history the
 *  pulse screen uses. */
function SnapshotPulse({ product }) {
  const base = product === "grow" ? "/grow" : "";
  const [state, setState] = useState(null);
  useEffect(() => {
    client.get(`/engagement/weekly-checkin?product=${product}`)
      .then((r) => setState(r.data || null))
      .catch(() => setState(null));
  }, [product]);
  if (!state) return null;
  const history = state.history || [];
  return (
    <div className="k-accent-card k-acc-teal mb-8 k-fade-in" data-testid="snapshot-pulse">
      <div className="flex items-center gap-2.5 mb-3">
        <span className="k-icon-tile k-icon-tile-sm" aria-hidden="true"><Activity size={16} /></span>
        <div className="k-acc-label">how your read is moving</div>
      </div>
      {history.length >= 1 ? (
        <>
          <p className="klareet-serif mb-4" style={{ fontSize: "1.1rem", color: "hsl(var(--klareet-ink))", lineHeight: 1.5 }} data-testid="snapshot-pulse-line">
            {pulseMovement(history)}
          </p>
          <PulseTrend history={history} questions={state.questions} />
          <div className="mt-4">
            <Link to={`${base}/pulse`} className="k-btn k-btn-ghost !py-1.5 !px-3 !text-sm" data-testid="snapshot-pulse-cta">
              Open your weekly pulse <ArrowRight size={13} />
            </Link>
          </div>
        </>
      ) : (
        <>
          <p className="klareet-serif mb-4" style={{ fontSize: "1.1rem", color: "hsl(var(--klareet-ink))", lineHeight: 1.5 }}>
            Your snapshot moves as you log a quick weekly pulse. Two taps a week is all it takes.
          </p>
          <Link to={`${base}/pulse`} className="k-btn k-btn-primary !text-sm" data-testid="snapshot-pulse-start">
            Start your weekly pulse <ArrowRight size={13} />
          </Link>
        </>
      )}
    </div>
  );
}

function SinceLastCheckin({ product }) {
  const nav = useNavigate();
  const base = product === "grow" ? "/grow" : "";
  const [mv, setMv] = useState(null);
  useEffect(() => {
    let alive = true;
    client.get(`/demo/read/movement?product=${product === "grow" ? "grow" : "start"}`)
      .then((r) => { if (alive) setMv(r.data); })
      .catch(() => { if (alive) setMv(null); });
    return () => { alive = false; };
  }, [product]);

  if (!mv) return null;
  const count = mv.read_count || 0;
  const firstRead = count < 2 || mv.view === "empty";

  return (
    <div className="k-accent-card k-acc-blue k-fade-in mb-8" style={{ animationDelay: "180ms" }} data-testid="since-last-checkin">
      <div className="flex items-center gap-3 mb-3">
        <span className="k-icon-tile" aria-hidden="true"><TrendingUp size={20} /></span>
        <div className="k-acc-label">since your last check in</div>
      </div>
      {firstRead ? (
        <div>
          <p className="klareet-serif" style={{ color: "hsl(var(--klareet-ink))", fontSize: "1rem", lineHeight: 1.55 }} data-testid="since-last-checkin-empty">
            This is your first read, so there is nothing to compare yet. Check in with Klair and this fills in with how each layer moved.
          </p>
          <button onClick={() => nav(`${base}/checkin`)} className="k-btn k-btn-ghost !py-1.5 !px-3 !text-sm mt-3" data-testid="since-last-checkin-cta">
            <Sparkles size={14} /> Check in with Klair
          </button>
        </div>
      ) : (
        <div>
          {mv.biggest_mover && (
            <p className="klareet-serif mb-3" style={{ color: "hsl(var(--klareet-ink))", fontSize: "1.05rem", lineHeight: 1.5 }} data-testid="since-last-checkin-mover">
              You moved most on {plainLayer(mv.biggest_mover.layer)} since last time.
            </p>
          )}
          <div className="flex flex-wrap gap-2" data-testid="since-last-checkin-rows">
            {(mv.rows || []).map((row) => {
              const d = typeof row.delta === "number" ? row.delta : null;
              const up = d !== null && d > 0.03;
              const down = d !== null && d < -0.03;
              const c = up ? "178 82% 34%" : down ? "28 92% 54%" : "230 15% 50%";
              const Ico = up ? TrendingUp : down ? TrendingDown : Minus;
              // Show the number only once there is a line (three reads or
              // more). At two reads a raw figure can misread as a real
              // drop, so the chip carries direction alone.
              const showNum = mv.view === "line" && d !== null;
              return (
                <span
                  key={row.layer}
                  className="k-chip !text-xs flex items-center gap-1"
                  style={{ color: `hsl(${c})`, background: `hsl(${c} / 0.1)`, border: "none" }}
                  data-testid={`since-row-${row.layer}`}
                >
                  <Ico size={11} /> {plainLayer(row.layer)}{showNum ? ` ${d > 0 ? "+" : ""}${d.toFixed(1)}` : ""}
                </span>
              );
            })}
          </div>
          {mv.view !== "line" && mv.honesty?.movement_not_trend && (
            <div className="k-quiet mt-3" style={{ fontSize: "0.8rem" }} data-testid="since-last-checkin-honesty">{mv.honesty.movement_not_trend}</div>
          )}
          {mv.honesty?.your_line_not_a_rank && (
            <div className="k-quiet mt-1" style={{ fontSize: "0.8rem" }}>{mv.honesty.your_line_not_a_rank}</div>
          )}
        </div>
      )}
    </div>
  );
}

function PredictionsCard({ predictions }) {
  // Klair's noticings. Curated slice only: the safety filter has
  // already dropped any entry that touches an integrity, distress,
  // careless, or contradiction tracker. Each entry carries exactly
  // { text, tested_at_turn_label, outcome_kind, evidence } and no
  // raw ledger data.
  return (
    <div
      className="k-fade-in mb-8"
      style={{ animationDelay: "400ms" }}
      data-testid="predictions-card"
    >
      <div className="k-accent-card k-acc-violet">
        <div className="flex items-center gap-3 mb-3">
          <span className="k-icon-tile" aria-hidden="true"><Sparkles size={20} /></span>
          <div className="k-acc-label">what Klair noticed early</div>
        </div>
        <p
          className="klareet-serif mb-4"
          style={{
            color: "hsl(var(--klareet-ink-soft))",
            fontSize: "1rem",
            lineHeight: 1.55,
          }}
        >
          Something small she noticed on the first few turns, and the moment later where you showed her it landed.
        </p>
        <div
          className="k-quiet mb-4"
          style={{ fontSize: "0.8rem" }}
          data-testid="predictions-footer"
        >
          Something Klair noticed, just for you. Not a verdict, not a rank.
        </div>
        <ul className="space-y-4" data-testid="predictions-list">
          {predictions.map((p, i) => (
            <li
              key={i}
              className="k-tint p-4"
              data-testid={`prediction-${i}`}
            >
              <p
                className="klareet-serif mb-2"
                style={{
                  color: "hsl(var(--klareet-ink))",
                  fontSize: "1rem",
                  lineHeight: 1.55,
                }}
                data-testid={`prediction-${i}-text`}
              >
                {p.text}
              </p>
              {p.tested_at_turn_label && (
                <div
                  className="k-quiet"
                  style={{ fontSize: "0.85rem" }}
                  data-testid={`prediction-${i}-tested`}
                >
                  {p.tested_at_turn_label}
                </div>
              )}
              {p.evidence && (
                <div
                  className="klareet-serif mt-2"
                  style={{
                    color: "hsl(var(--klareet-ink-soft))",
                    fontSize: "0.95rem",
                    lineHeight: 1.5,
                    fontStyle: "italic",
                  }}
                  data-testid={`prediction-${i}-evidence`}
                >
                  {p.evidence}
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function SectionBody({ layer, hue }) {
  if (!layer) {
    return <p className="k-quiet">Nothing to show here yet.</p>;
  }
  return (
    <div>
      <ul className="space-y-1.5" data-testid="section-constructs">
        {layer.top_constructs.map((c) => (
          <li key={c.construct} className="flex items-center justify-between gap-2 text-sm">
            <span style={{ color: "hsl(var(--klareet-ink-soft))" }}>{c.construct.replaceAll("_", " ")}</span>
            <span className="klareet-mono" style={{ color: `hsl(var(--k-${hue}))` }}>{Number(c.score).toFixed(2)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}


function NoSignalBody({ hue, subtitle }) {
  return (
    <div
      className="rounded-lg p-4"
      style={{
        background: `linear-gradient(135deg, hsl(var(--k-${hue}) / 0.06), hsl(var(--klareet-canvas-2)))`,
        border: "1px solid hsl(var(--klareet-line-soft))",
      }}
      data-testid="no-signal-body"
    >
      <div className="font-semibold mb-1" style={{ color: "hsl(var(--klareet-ink))" }}>
        Nothing to show here yet
      </div>
      <p className="text-sm" style={{ color: "hsl(var(--klareet-ink-soft))", lineHeight: 1.55 }}>
        {subtitle} did not come up enough in this chat to score. This is an early read, so an empty part is not a zero on you, it just means we have not heard enough yet.
        Tell Klair more next time and this part can fill in.
      </p>
    </div>
  );
}


function SituationBody({ intake, hue }) {
  if (!intake || !intake.has_intake) {
    return (
      <p className="k-quiet">No intake context saved for this read yet.</p>
    );
  }
  return (
    <div
      className="rounded-lg p-4"
      style={{
        background: `linear-gradient(135deg, hsl(var(--k-${hue}) / 0.06), hsl(var(--klareet-canvas-2)))`,
        border: "1px solid hsl(var(--klareet-line-soft))",
      }}
      data-testid="situation-body"
    >
      <div className="font-semibold mb-1" style={{ color: "hsl(var(--klareet-ink))" }}>
        What you told us about right now
      </div>
      <p className="text-sm mb-3" style={{ color: "hsl(var(--klareet-ink-soft))", lineHeight: 1.5 }}>
        This part is not scored. It is what is going on for you, in your own words. Every scored part sits inside this, so the numbers only make sense next to what you said here.
      </p>
      <ul className="space-y-3" data-testid="situation-items">
        {intake.items.map((it) => (
          <li key={it.key} className="text-sm" data-testid={`situation-${it.key}`}>
            <div className="k-label mb-1">{it.label}</div>
            <div
              className="klareet-serif"
              style={{ color: "hsl(var(--klareet-ink))", lineHeight: 1.5 }}
              data-testid={`situation-${it.key}-text`}
            >
              &ldquo;{it.text}&rdquo;
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}


function DerailerSealBody() {
  return (
    <div
      className="rounded-lg p-4"
      style={{
        background: "linear-gradient(135deg, hsl(var(--klareet-canvas-2)), hsl(var(--klareet-line-soft) / 0.5))",
        border: "1px solid hsl(var(--klareet-line))",
      }}
      data-testid="derailer-seal"
    >
      <div className="flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: "hsl(var(--klareet-ink))", color: "white" }}
        >
          <Shield size={16} />
        </div>
        <div>
          <div className="font-semibold" style={{ color: "hsl(var(--klareet-ink))" }}>Kept private</div>
          <p className="text-sm mt-1" style={{ color: "hsl(var(--klareet-ink-soft))" }}>
            Your watch-outs are just for you, to help you grow. They are never shared with your college or an employer, and never used to rank you. This part stays private here and opens only in a one to one session with a trained coach.
          </p>
        </div>
      </div>
    </div>
  );
}

function FullReadExtras({ report, product, savedTitles, onSaveBook }) {
  const cell = report.cell;
  const fitVal = Number(cell?.x_value ?? 0);
  const readVal = Number(cell?.y_value ?? 0);
  const fitPct  = Math.max(0, Math.min(100, Math.round(fitVal * 20)));
  const readPct = Math.max(0, Math.min(100, Math.round(readVal * 20)));
  return (
    <>
      <div className="grid sm:grid-cols-2 gap-5 mb-4 k-fade-in" style={{ animationDelay: "380ms" }}>
        <ScoreRing
          label={product === "grow" ? "Capability" : "Career fit"}
          pct={fitPct} raw={fitVal} tone="green"
          how={product === "grow"
            ? "Average of your four aptitude signals: verbal, numeric, spatial, and logical."
            : "The distance between your strongest interest and the average of the six. A higher number means one direction pulls harder than the rest."}
          next={product === "grow"
            ? "This moves as you cite specific examples of hard problems you have solved."
            : "This moves as you speak more about the one direction that keeps returning in your answers."}
        />
        <ScoreRing
          label={product === "grow" ? "Positioning" : "Career readiness"}
          pct={readPct} raw={readVal} tone="violet"
          how="Average of three signals about carrying a plan through: careful and steady, even under strain, and growth-seeking."
          next="This moves as you talk about how you followed through on a plan, or how you steadied yourself when things wobbled."
        />
      </div>
      <div className="k-accent-card k-acc-blue mb-8 k-fade-in" style={{ animationDelay: "440ms" }}>
        <div className="flex items-center gap-2.5 mb-2">
          <span className="k-icon-tile k-icon-tile-sm" aria-hidden="true"><Target size={16} /></span>
          <div className="k-acc-label">{product === "grow" ? "Capability and positioning" : "Fit and readiness"}</div>
        </div>
        <p className="klareet-serif" style={{ fontSize: "1.3rem", lineHeight: 1.4 }} data-testid="cell-name">
          {cell?.quadrant_or_cell}
        </p>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <MetricCell label={cell?.x_label} value={cell?.x_value} />
          <MetricCell label={cell?.y_label} value={cell?.y_value} />
        </div>
        <div
          className="rounded-lg p-3 mt-4 text-sm"
          style={{ background: "hsl(var(--klareet-canvas-2))", border: "1px solid hsl(var(--klareet-line-soft))", lineHeight: 1.55, color: "hsl(var(--klareet-ink-soft))" }}
          data-testid="cell-explainer"
        >
          <div className="font-semibold mb-1" style={{ color: "hsl(var(--klareet-ink))" }}>How we got here</div>
          Both numbers are averages on the 0.0 to 5.0 scale, from your own answers. The name above just pairs the two in plain words, and it never sorts you against anyone else. This is an early read, so it can move as you say more.
        </div>
      </div>

      <div className="k-accent-card k-acc-teal mb-8">
        <div className="flex items-center gap-2.5 mb-3">
          <span className="k-icon-tile k-icon-tile-sm" aria-hidden="true"><Map size={16} /></span>
          <div className="k-acc-label">Where you are, where you are heading</div>
        </div>
        <ul className="space-y-2 text-sm">
          {report.gap.top_gaps.map((g) => (
            <li key={g.anchor} className="flex justify-between gap-2" data-testid={`gap-${g.anchor}`}>
              <span style={{ color: "hsl(var(--klareet-ink-soft))" }}>{g.anchor.replaceAll("_", " ")}</span>
              <span className="klareet-mono">+{g.gap}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="k-accent-card k-acc-blue mb-8">
        <div className="flex items-center gap-2.5 mb-4">
          <span className="k-icon-tile k-icon-tile-sm" aria-hidden="true"><Map size={16} /></span>
          <div className="k-acc-label">Twelve week roadmap</div>
        </div>
        <ol className="relative space-y-3 pl-4" style={{ borderLeft: "2px dashed hsl(var(--klareet-line))" }}>
          {report.roadmap.map((r) => (
            <li key={r.week_from_start} className="pl-4 relative" data-testid={`roadmap-w${r.week_from_start}`}>
              <span className="absolute -left-[9px] top-1 w-4 h-4 rounded-full flex items-center justify-center"
                    style={{ background: "hsl(var(--klareet-blue))", color: "white", fontSize: 9 }}>
                <span className="klareet-mono">W{r.week_from_start}</span>
              </span>
              <div className="pl-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium" style={{ color: "hsl(var(--klareet-ink))" }} data-testid={`roadmap-w${r.week_from_start}-theme`}>
                    {r.theme}
                  </span>
                  {r.anchor && (
                    <span
                      className="klareet-mono !text-[10px] px-2 py-0.5 rounded-full"
                      style={{
                        color: "hsl(var(--klareet-muted))",
                        background: "hsl(var(--klareet-canvas-2))",
                        border: "1px solid hsl(var(--klareet-line-soft))",
                      }}
                      data-testid={`roadmap-w${r.week_from_start}-anchor`}
                    >
                      {r.anchor.replaceAll("_", " ")}
                    </span>
                  )}
                </div>
                <div className="k-quiet">{r.action}</div>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {Array.isArray(report.books) && report.books.length > 0 && (
        <div className="k-accent-card k-acc-violet mb-8" data-testid="books-card">
          <div className="flex items-center gap-2.5 mb-3">
            <span className="k-icon-tile k-icon-tile-sm" aria-hidden="true"><BookOpen size={16} /></span>
            <div className="k-acc-label">Five books, picked for what you are working on</div>
          </div>

          {/* Cover collage strip. Real jackets from Open Library
              (covers.openlibrary.org). Each cover falls back to a
              plain plate on load-error so a missing image never
              breaks the row. */}
          <div className="mb-4 flex gap-3 overflow-x-auto pb-2" data-testid="books-collage">
            {report.books.map((b, i) => (
              <BookCover
                key={`cover-${b.title}-${i}`}
                book={b}
                index={i}
                variant="collage"
              />
            ))}
          </div>

          <ol className="space-y-3">
            {report.books.map((b, i) => {
              const isSaved = savedTitles && savedTitles.has(b.title);
              return (
              <li
                key={`${b.title}-${i}`}
                className="flex gap-3 rounded-xl p-3"
                style={{
                  background: "hsl(var(--klareet-canvas-2))",
                  border: "1px solid hsl(var(--klareet-line-soft))",
                }}
                data-testid={`book-${i}`}
              >
                <span
                  className="klareet-mono text-xs shrink-0 mt-0.5"
                  style={{ color: "hsl(var(--klareet-muted))", width: 18 }}
                >
                  {i + 1}.
                </span>
                <BookCover book={b} index={i} variant="row" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-semibold" style={{ color: "hsl(var(--klareet-ink))" }} data-testid={`book-${i}-title`}>
                      {b.title}
                    </span>
                    <span className="k-quiet !text-xs" data-testid={`book-${i}-author`}>
                      by {b.author}
                    </span>
                    {b.anchor && (
                      <span
                        className="klareet-mono !text-[10px] px-2 py-0.5 rounded-full"
                        style={{
                          color: "hsl(var(--klareet-muted))",
                          background: "hsl(var(--card))",
                          border: "1px solid hsl(var(--klareet-line-soft))",
                        }}
                        data-testid={`book-${i}-anchor`}
                      >
                        {b.anchor.replaceAll("_", " ")}
                      </span>
                    )}
                  </div>
                  <div className="k-quiet !text-sm mt-1" data-testid={`book-${i}-why`}>
                    {b.why}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onSaveBook && onSaveBook(b)}
                  disabled={isSaved}
                  className="k-btn-outline !text-xs shrink-0 h-fit print:hidden"
                  data-testid={`book-${i}-save`}
                  aria-label={isSaved ? "Saved to your reading rail" : "Save to your reading rail"}
                  title={isSaved ? "On your reading rail" : "Save to your reading rail"}
                >
                  {isSaved ? (
                    <><BookmarkCheck size={12} /> Saved</>
                  ) : (
                    <><Bookmark size={12} /> Save</>
                  )}
                </button>
              </li>
              );
            })}
          </ol>
          <p className="k-quiet !text-xs mt-3">
            Picks come from your top gaps and your archetype. Books are suggestions, not a ranking.
          </p>
        </div>
      )}

      <div className="k-accent-card k-acc-amber">
        <div className="flex items-center gap-2.5 mb-3">
          <span className="k-icon-tile k-icon-tile-sm" aria-hidden="true"><BookOpen size={16} /></span>
          <div className="k-acc-label">What to study, and where</div>
        </div>
        <ul className="space-y-3">
          {report.learning.map((l, i) => (
            <li key={i} className="flex gap-3" data-testid={`learning-${i}`}>
              <Lightbulb size={16} className="mt-1" style={{ color: "hsl(var(--klareet-amber))" }} />
              <div>
                <div className="font-medium" style={{ color: "hsl(var(--klareet-ink))" }}>{l.topic}</div>
                <div className="k-quiet">{l.reason}. Try: {l.where}.</div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Print-only footer. Rendered on paper only via
          `.k-print-footer` in the global stylesheet, so the person
          can share the print without losing the standing framing. */}
      <div className="k-print-footer" data-testid="report-print-footer">
        Klareet private read. This is a personal growth snapshot, not a formal test result. It was made for the person named above and is theirs to share as they choose.
      </div>
    </>
  );
}

function MetricCell({ label, value }) {
  return (
    <div className="rounded-xl p-3" style={{ background: "hsl(var(--klareet-canvas-2))", border: "1px solid hsl(var(--klareet-line-soft))" }}>
      <div className="k-label">{label}</div>
      <div className="klareet-mono mt-1" style={{ fontSize: "1.1rem", color: "hsl(var(--klareet-ink))" }}>{value}</div>
    </div>
  );
}

function ScoreRing({ label, pct, raw, tone, how, next }) {
  // KLA-060: use the design tokens so the ring matches the card accent
  // instead of a hardcoded greener teal / off violet.
  const color = tone === "green" ? "hsl(var(--klareet-teal))" : "hsl(var(--klareet-violet))";
  const accClass = tone === "green" ? "k-acc-teal" : "k-acc-violet";
  const r = 44;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;
  const testid = `ring-${label.toLowerCase().replaceAll(" ", "-")}`;
  return (
    <div className={`k-accent-card ${accClass}`} data-testid={testid}>
      <div className="flex items-center gap-5">
        <svg width="108" height="108" viewBox="0 0 108 108">
          <circle cx="54" cy="54" r={r} fill="none" stroke="hsl(var(--klareet-line))" strokeWidth="10" />
          <circle cx="54" cy="54" r={r} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
                  strokeDasharray={`${dash} ${c - dash}`}
                  transform="rotate(-90 54 54)" />
          <text x="54" y="52" textAnchor="middle" fontFamily="JetBrains Mono" fontSize="22" fill="hsl(var(--klareet-ink))">
            {pct}
          </text>
          <text x="54" y="68" textAnchor="middle" fontFamily="JetBrains Mono" fontSize="9" fill="hsl(var(--klareet-muted))">
            of 100
          </text>
        </svg>
        <div>
          <div className="k-label" style={{ color }}>{label}</div>
          <div className="klareet-mono mt-1 k-quiet">{raw?.toFixed?.(2) ?? raw} / 5.00</div>
        </div>
      </div>
      {(how || next) && (
        <div
          className="mt-4 rounded-lg p-3 text-xs space-y-2"
          style={{ background: "hsl(var(--klareet-canvas-2))", border: "1px solid hsl(var(--klareet-line-soft))", lineHeight: 1.55 }}
          data-testid={`${testid}-provenance`}
        >
          {how && (
            <div>
              <span className="font-semibold" style={{ color: "hsl(var(--klareet-ink))" }}>How we got this: </span>
              <span style={{ color: "hsl(var(--klareet-ink-soft))" }}>{how}</span>
            </div>
          )}
          {next && (
            <div>
              <span className="font-semibold" style={{ color: "hsl(var(--klareet-ink))" }}>What moves it: </span>
              <span style={{ color: "hsl(var(--klareet-ink-soft))" }}>{next}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ArchCard({ label, arch, tone, testid, delay }) {
  if (!arch) return null;
  const accClass = tone === "blue" ? "k-acc-blue" : "k-acc-violet";
  return (
    <div
      className={`k-accent-card ${accClass} k-fade-in`}
      style={{ animationDelay: `${delay || 0}ms` }}
      data-testid={testid}
    >
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="min-w-0">
          <div className="k-acc-label mb-2">{label}</div>
          <h3 className="klareet-serif" style={{ fontSize: "1.75rem", lineHeight: 1.12 }}>{arch.name}</h3>
        </div>
        <span className="k-icon-tile" aria-hidden="true">
          <ArchetypeGlyph name={arch.name} />
        </span>
      </div>
      <div
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold mb-3"
        style={{ background: "hsl(var(--acc) / 0.12)", color: "hsl(var(--acc))", border: "1px solid hsl(var(--acc) / 0.25)", letterSpacing: "0.04em", textTransform: "uppercase" }}
      >
        {arch.code} <Sparkles size={10} /> {arch.tag}
      </div>
      <p className="text-sm" style={{ color: "hsl(var(--klareet-ink-soft))" }}>{arch.blurb}</p>
      {arch.why_fit && (
        <p className="text-sm mt-3 klareet-serif" style={{ color: "hsl(var(--klareet-ink))", lineHeight: 1.5 }} data-testid={`${testid}-why-fit`}>
          {arch.why_fit}
        </p>
      )}
      {Array.isArray(arch.roles) && arch.roles.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5" data-testid={`${testid}-roles`}>
          {arch.roles.slice(0, 4).map((r, i) => (
            <span key={i} className="px-2.5 py-1 rounded-full text-xs k-glass" style={{ color: "hsl(var(--klareet-ink))" }}>
              {r}
            </span>
          ))}
        </div>
      )}
      {(arch.fit_score || 0) > 0 && (
        <div className="mt-4 pt-3 flex items-center justify-between" style={{ borderTop: "1px solid hsl(var(--klareet-line-soft))" }}>
          <span className="k-quiet !text-xs">how closely this reads like you</span>
          <span
            className="klareet-mono text-sm px-2.5 py-1 rounded-full"
            style={{ color: "hsl(var(--acc))", background: "hsl(var(--acc) / 0.12)" }}
            data-testid={`${testid}-match`}
          >
            {Math.round((arch.fit_score / 5) * 100)}% match
          </span>
        </div>
      )}
    </div>
  );
}



/**
 * BookCover
 *
 * Renders a real jacket from Open Library when a cover_url is
 * present, and falls back to a plain plate (initials over the
 * anchor colour) when the image fails to load or the row is
 * missing an ISBN. Two size variants:
 *   - "collage"  large tile for the horizontal strip at the top
 *   - "row"      small plate that sits inside each numbered row
 */
function BookCover({ book, index, variant }) {
  const [failed, setFailed] = useState(false);
  const cover = book.cover_url || "";
  const initials = (book.title || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  const isCollage = variant === "collage";
  const size = isCollage
    ? { w: 90, h: 132 }
    : { w: 42, h: 62 };

  const style = {
    width: size.w,
    height: size.h,
    borderRadius: 6,
    boxShadow: isCollage
      ? "0 8px 20px hsl(var(--klareet-ink) / 0.10)"
      : "0 2px 6px hsl(var(--klareet-ink) / 0.08)",
    background: "hsl(var(--klareet-canvas-2))",
    border: "1px solid hsl(var(--klareet-line-soft))",
    overflow: "hidden",
    flexShrink: 0,
  };

  if (cover && !failed) {
    return (
      <img
        src={cover}
        alt={`${book.title} cover`}
        loading="lazy"
        onError={() => setFailed(true)}
        style={{ ...style, objectFit: "cover" }}
        data-testid={`book-${index}-cover`}
      />
    );
  }

  return (
    <div
      style={{
        ...style,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      data-testid={`book-${index}-cover-fallback`}
      aria-label={`${book.title} placeholder cover`}
    >
      <span
        className="klareet-serif"
        style={{
          color: "hsl(var(--klareet-muted))",
          fontSize: isCollage ? 22 : 12,
          letterSpacing: "0.06em",
        }}
      >
        {initials}
      </span>
    </div>
  );
}

// ===================================================================
// Snapshot tabs v2. Colorful, plain, Gen Z friendly panels. Every
// value maps to real read data. No fabricated metrics.
// ===================================================================

const BAR_ACCENTS = ["var(--klareet-blue)", "var(--klareet-teal)", "var(--klareet-violet)", "var(--klareet-amber)"];

function TopSummaryCard({ report }) {
  const A = report.archetype;
  const bars = (report.layers || [])
    .map((l) => { const m = matchLayer(l.layer); const v = layerScore(l); return m && v != null ? { label: m.label, value: v } : null; })
    .filter(Boolean)
    .sort((a, b) => b.value - a.value)
    .slice(0, 4);
  const sides = (report.layers || []).length || 6;
  return (
    <div className="k-accent-card k-acc-ink k-fade-in mb-8" data-testid="top-summary-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="k-acc-label mb-2">your read</div>
          <h2 className="klareet-serif" style={{ fontSize: "clamp(2rem, 4.5vw, 3rem)", lineHeight: 1.05, letterSpacing: "-0.02em" }}>
            {A?.primary?.name}
          </h2>
          <p className="text-base mt-1" style={{ color: "hsl(var(--klareet-ink-soft))" }}>{A?.primary?.tag}</p>
        </div>
        <span className="k-chip" data-testid="summary-sides-pill">{sides} sides</span>
      </div>
      <div className="k-gradient-bar my-6" />
      <div className="space-y-4" data-testid="summary-bars">
        {bars.map((b, i) => (
          <div key={b.label} data-testid={`summary-bar-${i}`}>
            <div className="flex justify-between text-sm mb-1.5">
              <span style={{ color: "hsl(var(--klareet-ink))" }}>{b.label}</span>
              <span className="klareet-mono k-quiet">{b.value.toFixed(1)}</span>
            </div>
            <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "hsl(var(--klareet-line-soft))" }}>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${Math.max(4, (b.value / 5) * 100)}%` }}
                transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                className="h-full rounded-full"
                style={{ background: `linear-gradient(90deg, hsl(${BAR_ACCENTS[i % 4]}), hsl(${BAR_ACCENTS[i % 4]} / 0.65))` }}
              />
            </div>
          </div>
        ))}
      </div>
      <p className="k-quiet !text-sm mt-6 pt-4" style={{ borderTop: "1px solid hsl(var(--klareet-line-soft))" }}>
        Next up: a twelve week plan and a resume, over in your Roadmap.
      </p>
    </div>
  );
}

function CareerFitPanel({ report, product }) {
  const A = report.archetype;
  const roles = [...new Set([...(A?.primary?.roles || []), ...(A?.secondary?.roles || [])])];
  const [role, setRole] = useState(roles[0] || A?.primary?.name || "your path");
  const cell = report.cell;
  const fitVal = Number(cell?.x_value ?? 0);
  const readVal = Number(cell?.y_value ?? 0);
  const fitPct = Math.max(0, Math.min(100, Math.round(fitVal * 20)));
  const readPct = Math.max(0, Math.min(100, Math.round(readVal * 20)));
  return (
    <div className="k-fade-in">
      <div className="k-accent-card k-acc-violet mb-6" data-testid="career-explore-card">
        <div className="k-acc-label mb-2">a path that fits you</div>
        <h2 className="klareet-serif" style={{ fontSize: "clamp(2rem, 5vw, 3.4rem)", lineHeight: 1.04, letterSpacing: "-0.02em" }} data-testid="career-big-name">
          {role}
        </h2>
        <p className="text-base mt-2" style={{ color: "hsl(var(--klareet-ink-soft))", maxWidth: "42rem" }}>
          {A?.primary?.why_fit || "This lines up with how you answered."}
        </p>
        {roles.length > 1 && (
          <div className="mt-5" data-testid="role-explorer">
            <div className="k-quiet !text-xs mb-2">Not sure yet? Tap around. These all fit your read.</div>
            <div className="flex flex-wrap gap-2">
              {roles.map((r) => (
                <button
                  key={r} type="button" onClick={() => setRole(r)}
                  data-testid={`role-chip-${r.toLowerCase().replaceAll(" ", "-")}`}
                  className="px-3.5 py-2 rounded-full text-sm border transition-all duration-200 hover:-translate-y-0.5"
                  style={{ background: role === r ? "hsl(var(--klareet-violet))" : "hsl(var(--klareet-canvas-2))", color: role === r ? "#fff" : "hsl(var(--klareet-ink))", borderColor: role === r ? "hsl(var(--klareet-violet))" : "hsl(var(--klareet-line-soft))" }}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mb-6">
        <ScoreRing
          label={product === "grow" ? "Capability" : "Career fit"} pct={fitPct} raw={fitVal} tone="green"
          how={product === "grow" ? "Average of your four aptitude signals: verbal, numeric, spatial, and logical." : "How far your strongest interest sits from the average of your six sides. Higher means one direction pulls harder than the rest."}
          next={product === "grow" ? "This moves as you cite specific hard problems you have solved." : "This moves as you say more about the one direction that keeps coming back."}
        />
        <ScoreRing
          label={product === "grow" ? "Positioning" : "Career readiness"} pct={readPct} raw={readVal} tone="violet"
          how="Three signals about carrying a plan through: careful and steady, steady under strain, and up for growth."
          next="This moves as you talk about following through, or steadying yourself when things wobble."
        />
      </div>

      <div className="k-accent-card k-acc-blue mb-6" data-testid="fit-against">
        <div className="flex items-center gap-2.5 mb-2">
          <span className="k-icon-tile k-icon-tile-sm" aria-hidden="true"><Target size={16} /></span>
          <div className="k-acc-label">{product === "grow" ? "Capability and positioning" : "Fit and readiness"}</div>
        </div>
        <p className="klareet-serif" style={{ fontSize: "1.3rem", lineHeight: 1.4 }} data-testid="cell-name">{cell?.quadrant_or_cell}</p>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <MetricCell label={cell?.x_label} value={cell?.x_value} />
          <MetricCell label={cell?.y_label} value={cell?.y_value} />
        </div>
        <div className="rounded-lg p-3 mt-4 text-sm" style={{ background: "hsl(var(--klareet-canvas-2))", border: "1px solid hsl(var(--klareet-line-soft))", lineHeight: 1.55, color: "hsl(var(--klareet-ink-soft))" }} data-testid="cell-explainer">
          <div className="font-semibold mb-1" style={{ color: "hsl(var(--klareet-ink))" }}>What this is measured against</div>
          Both numbers sit on a 0.0 to 5.0 scale, all from your own answers. Fit is you against your own other directions, never against other people. Readiness is you against a simple bar for follow through. This is an early read, so it moves as you say more.
        </div>
      </div>

      <WhereHeadingFlow report={report} />
      <Recommendations report={report} />
    </div>
  );
}

function WhereHeadingFlow({ report }) {
  const strengths = (report.layers || [])
    .map((l) => { const m = matchLayer(l.layer); const v = layerScore(l); return m && v != null ? { label: m.label, v } : null; })
    .filter(Boolean).sort((a, b) => b.v - a.v).slice(0, 2);
  const gaps = (report.gap?.top_gaps || []).slice(0, 2);
  return (
    <div className="k-accent-card k-acc-teal mb-6" data-testid="where-heading">
      <div className="flex items-center gap-2.5 mb-4">
        <span className="k-icon-tile k-icon-tile-sm" aria-hidden="true"><Map size={16} /></span>
        <div className="k-acc-label">Where you are, where you are heading</div>
      </div>
      <div className="grid md:grid-cols-[1fr_auto_1fr] gap-4 items-stretch">
        <div className="k-tint p-4" style={{ "--acc": "var(--klareet-teal)" }} data-testid="where-now">
          <div className="flex items-center gap-2 mb-2"><MapPin size={15} style={{ color: "hsl(var(--klareet-teal))" }} /><span className="font-semibold" style={{ color: "hsl(var(--klareet-ink))" }}>Where you are now</span></div>
          <p className="text-sm mb-3" style={{ color: "hsl(var(--klareet-ink-soft))" }}>{report.context || "You just finished your read, so you know your sides."}</p>
          <div className="flex flex-wrap gap-1.5">
            {strengths.map((s) => (<span key={s.label} className="px-2.5 py-1 rounded-full text-xs k-glass" style={{ color: "hsl(var(--klareet-ink))" }}>{s.label}</span>))}
          </div>
        </div>
        <div className="hidden md:flex items-center justify-center" aria-hidden="true">
          <motion.div initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.3 }}>
            <ArrowRight size={26} style={{ color: "hsl(var(--klareet-violet))" }} />
          </motion.div>
        </div>
        <div className="k-tint p-4" style={{ "--acc": "var(--klareet-violet)" }} data-testid="where-target">
          <div className="flex items-center gap-2 mb-2"><Flag size={15} style={{ color: "hsl(var(--klareet-violet))" }} /><span className="font-semibold" style={{ color: "hsl(var(--klareet-ink))" }}>Where you are heading</span></div>
          <p className="text-sm mb-3" style={{ color: "hsl(var(--klareet-ink-soft))" }}>{report.goal_model?.next_move || "Toward the direction that keeps coming back in your answers."}</p>
          <div className="flex flex-wrap gap-1.5">
            {gaps.map((g) => (<span key={g.anchor} className="px-2.5 py-1 rounded-full text-xs k-glass" style={{ color: "hsl(var(--klareet-ink))" }}>grow your {g.anchor.replaceAll("_", " ")}</span>))}
          </div>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3 mt-5">
        <div className="rounded-lg p-3 text-sm" style={{ background: "hsl(var(--klareet-canvas-2))", border: "1px solid hsl(var(--klareet-line-soft))" }}>
          <div className="k-acc-label mb-1" style={{ "--acc": "var(--klareet-teal)" }}>what this means</div>
          <span style={{ color: "hsl(var(--klareet-ink-soft))" }}>You are strong where it counts. A couple of sides just need reps.</span>
        </div>
        <div className="rounded-lg p-3 text-sm" style={{ background: "hsl(var(--klareet-canvas-2))", border: "1px solid hsl(var(--klareet-line-soft))" }}>
          <div className="k-acc-label mb-1" style={{ "--acc": "var(--klareet-blue)" }}>why Klair reads it this way</div>
          <span style={{ color: "hsl(var(--klareet-ink-soft))" }}>Your top sides came through clearly, and these gaps showed up softer.</span>
        </div>
        <div className="rounded-lg p-3 text-sm" style={{ background: "hsl(var(--klareet-canvas-2))", border: "1px solid hsl(var(--klareet-line-soft))" }}>
          <div className="k-acc-label mb-1" style={{ "--acc": "var(--klareet-violet)" }}>based on what</div>
          <span style={{ color: "hsl(var(--klareet-ink-soft))" }}>Your own answers across the chat. The more you say, the sharper it gets.</span>
        </div>
      </div>
    </div>
  );
}

function Recommendations({ report }) {
  const recs = [];
  if (report.goal_model?.next_move) recs.push({ icon: Target, text: report.goal_model.next_move, tag: "your next move" });
  (report.gap?.top_gaps || []).slice(0, 2).forEach((g) => recs.push({ icon: TrendingUp, text: `Put a rep in on your ${g.anchor.replaceAll("_", " ")}. One small real thing beats a big plan.`, tag: "close a gap" }));
  if ((report.books || []).length) recs.push({ icon: BookOpen, text: `Grab "${report.books[0].title}" from your Library and read the first chapter this week.`, tag: "learn" });
  if (!recs.length) return null;
  return (
    <div className="k-accent-card k-acc-amber mb-2" data-testid="recommendations">
      <div className="flex items-center gap-2.5 mb-4">
        <span className="k-icon-tile k-icon-tile-sm" aria-hidden="true"><CheckCircle2 size={16} /></span>
        <div className="k-acc-label">a few things to try next</div>
      </div>
      <div className="space-y-3">
        {recs.map((r, i) => (
          <div key={i} className="k-tint p-4 flex items-start gap-3" style={{ "--acc": "var(--klareet-amber)" }} data-testid={`rec-${i}`}>
            <span className="k-icon-tile k-icon-tile-sm shrink-0" aria-hidden="true"><r.icon size={15} /></span>
            <div>
              <div className="k-acc-label mb-1" style={{ "--acc": "var(--klareet-amber)" }}>{r.tag}</div>
              <p className="text-sm" style={{ color: "hsl(var(--klareet-ink))" }}>{r.text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const PRESET_GOALS = [
  { key: "internship", label: "Internship Ready", icon: Briefcase, blurb: "Land a solid internship. Build a little proof, tighten your resume, and get a few interview reps in." },
  { key: "first-job", label: "First Job Ready", icon: Target, blurb: "Aim for your first full role. Skills that show, a small portfolio, and a story that fits the job." },
  { key: "placement", label: "Placement Ready", icon: GraduationCap, blurb: "Campus placements are close. Get sharp on aptitude, two strong projects, and the interview loop." },
  { key: "higher-studies", label: "Higher Studies Ready", icon: Layers, blurb: "Going for a masters or more. Build the profile, prep the tests, and line up the applications." },
  { key: "switch-stream", label: "Switch My Stream", icon: Shuffle, blurb: "You want a lane your degree did not give you. Test it cheap, build proof, then make the move." },
  { key: "startup", label: "Startup Curious", icon: Rocket, blurb: "You have got an itch to build. Pick one idea, ship something tiny, and learn fast from real people." },
];

function PathPanel({ report, goal, onGoRoadmap }) {
  const [dropped, setDropped] = useState(() => new Set());
  const [open, setOpen] = useState(PRESET_GOALS[0].key);
  const goalText = goal?.next_move ? "your goal" : "your real world goal";
  const stages = [
    { icon: MapPin, title: "Know yourself", body: "You are here. Your read is done, so you know your sides.", tag: "Start" },
    { icon: TrendingUp, title: "Build proof", body: "Ship small real things that show what you can do." },
    { icon: CheckCircle2, title: "Get ready", body: "Resume, a tidy portfolio, and a few interview reps." },
    { icon: Flag, title: "Make your move", body: "Land the internship, the job, or your first customer." },
    { icon: Rocket, title: "Grow at work", body: "You are in the door. Now level up and keep going.", tag: "Grow" },
  ];
  return (
    <div className="k-fade-in">
      <div className="k-accent-card k-acc-teal mb-6" data-testid="preset-goals">
        <div className="flex items-center gap-2.5 mb-2">
          <span className="k-icon-tile k-icon-tile-sm" aria-hidden="true"><Target size={16} /></span>
          <div className="k-acc-label">pick a goal to aim at</div>
        </div>
        <p className="k-quiet !text-sm mb-4">These are here by default. Keep the ones that fit, drop the rest. Each one opens into a real plan on your Roadmap.</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {PRESET_GOALS.filter((g) => !dropped.has(g.key)).map((g) => (
            <div
              key={g.key}
              className="inline-flex items-center gap-1 pl-3 pr-1.5 py-1 rounded-full text-sm border transition-all duration-200 hover:-translate-y-0.5"
              style={{ background: open === g.key ? "hsl(var(--klareet-teal))" : "hsl(var(--klareet-canvas-2))", color: open === g.key ? "#fff" : "hsl(var(--klareet-ink))", borderColor: open === g.key ? "hsl(var(--klareet-teal))" : "hsl(var(--klareet-line-soft))" }}
            >
              <button
                type="button"
                onClick={() => setOpen(open === g.key ? null : g.key)}
                data-testid={`preset-${g.key}`}
                className="inline-flex items-center gap-2 py-1"
                style={{ color: "inherit", background: "transparent" }}
              >
                <g.icon size={14} /> {g.label}
              </button>
              <button
                type="button"
                aria-label={`Remove ${g.label}`}
                data-testid={`preset-${g.key}-remove`}
                onClick={() => { setDropped((d) => new Set(d).add(g.key)); if (open === g.key) setOpen(null); }}
                className="rounded-full p-1 opacity-70 hover:opacity-100"
                style={{ color: "inherit", lineHeight: 0 }}
              >
                <X size={13} />
              </button>
            </div>
          ))}
        </div>
        {PRESET_GOALS.filter((g) => !dropped.has(g.key) && g.key === open).map((g) => (
          <div key={g.key} className="k-tint p-4" style={{ "--acc": "var(--klareet-teal)" }} data-testid={`preset-panel-${g.key}`}>
            <div className="flex items-center gap-2 mb-1"><g.icon size={16} style={{ color: "hsl(var(--klareet-teal))" }} /><span className="font-semibold" style={{ color: "hsl(var(--klareet-ink))" }}>{g.label}</span></div>
            <p className="text-sm mb-3" style={{ color: "hsl(var(--klareet-ink-soft))" }}>{g.blurb}</p>
            <button type="button" onClick={onGoRoadmap} className="k-btn k-btn-primary !text-sm" data-testid={`preset-${g.key}-start`}>
              Build this on my Roadmap <ArrowRight size={14} />
            </button>
          </div>
        ))}
      </div>

      <div className="k-accent-card k-acc-blue mb-6" data-testid="start-to-grow">
        <div className="flex items-center gap-2.5 mb-2">
          <span className="k-icon-tile k-icon-tile-sm" aria-hidden="true"><Map size={16} /></span>
          <div className="k-acc-label">Start to Grow</div>
        </div>
        <p className="k-quiet !text-sm mb-6">From where you are in college now, to ready for {goalText}. Here is the shape of it. The real week by week plan lives on your Roadmap.</p>
        <div className="grid gap-4 md:grid-cols-5" data-testid="start-to-grow-stops">
          {stages.map((s, i) => (
            <motion.div
              key={s.title} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.1 }}
              className="k-tint p-4 relative" style={{ "--acc": i === 0 ? "var(--klareet-teal)" : i === 4 ? "var(--klareet-violet)" : "var(--klareet-blue)" }} data-testid={`stg-${i}`}
            >
              {s.tag && (<span className="absolute -top-2 right-3 klareet-mono text-[10px] px-2 py-0.5 rounded-full" style={{ background: i === 0 ? "hsl(var(--klareet-teal))" : "hsl(var(--klareet-violet))", color: "#fff" }}>{s.tag}</span>)}
              <span className="k-icon-tile k-icon-tile-sm mb-2" aria-hidden="true"><s.icon size={15} /></span>
              <div className="font-semibold text-sm" style={{ color: "hsl(var(--klareet-ink))" }}>{i + 1}. {s.title}</div>
              <p className="k-quiet !text-xs mt-1">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </div>

      <Recommendations report={report} />

      <div className="k-accent-card k-acc-violet flex items-center justify-between gap-4 flex-wrap" data-testid="path-roadmap-cta">
        <div>
          <div className="klareet-serif" style={{ fontSize: "1.25rem" }}>Ready to make it real?</div>
          <p className="k-quiet !text-sm">Your week by week plan and progress live on the Roadmap.</p>
        </div>
        <button type="button" onClick={onGoRoadmap} className="k-btn k-btn-primary" data-testid="go-roadmap-btn">
          Open my Roadmap <ArrowRight size={15} />
        </button>
      </div>
    </div>
  );
}

function LibraryPanel({ report, savedTitles, onSaveBook }) {
  const books = report.books || [];
  const learning = report.learning || [];
  return (
    <div className="k-fade-in">
      <div className="k-accent-card k-acc-violet mb-6" data-testid="library-card">
        <div className="flex items-center gap-2.5 mb-2">
          <span className="k-icon-tile k-icon-tile-sm" aria-hidden="true"><BookOpen size={16} /></span>
          <div className="k-acc-label">your shelf</div>
        </div>
        <p className="k-quiet !text-sm mb-6">Picked from your top gaps and your read. Suggestions, never a ranking.</p>
        {books.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="library-shelf">
              {books.map((b, i) => {
                const isSaved = savedTitles && savedTitles.has(b.title);
                return (
                  <div key={`${b.title}-${i}`} className="flex flex-col" data-testid={`shelf-book-${i}`}>
                    <div className="k-book-hover self-start"><BookCover book={b} index={i} variant="collage" /></div>
                    <div className="mt-3 flex-1">
                      <div className="font-semibold text-sm leading-snug" style={{ color: "hsl(var(--klareet-ink))" }} data-testid={`shelf-book-${i}-title`}>{b.title}</div>
                      <div className="k-quiet !text-xs mt-0.5">by {b.author}</div>
                      <div className="k-quiet !text-xs mt-1.5" style={{ lineHeight: 1.5 }}>{b.why}</div>
                    </div>
                    <button type="button" onClick={() => onSaveBook && onSaveBook(b)} disabled={isSaved} className="k-btn-outline !text-xs mt-3 self-start print:hidden" data-testid={`shelf-book-${i}-save`}>
                      {isSaved ? (<><BookmarkCheck size={12} /> Saved</>) : (<><Bookmark size={12} /> Save</>)}
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="k-shelf-plank mt-4" aria-hidden="true" />
          </>
        ) : (<p className="k-quiet">No books yet.</p>)}
      </div>

      {learning.length > 0 && (
        <div className="k-accent-card k-acc-amber" data-testid="learning-card">
          <div className="flex items-center gap-2.5 mb-3">
            <span className="k-icon-tile k-icon-tile-sm" aria-hidden="true"><Lightbulb size={16} /></span>
            <div className="k-acc-label">what to study, and where</div>
          </div>
          <ul className="space-y-3">
            {learning.map((l, i) => (
              <li key={i} className="flex gap-3" data-testid={`learning-${i}`}>
                <Lightbulb size={16} className="mt-1" style={{ color: "hsl(var(--klareet-amber))" }} />
                <div>
                  <div className="font-medium" style={{ color: "hsl(var(--klareet-ink))" }}>{l.topic}</div>
                  <div className="k-quiet">{l.reason}. Try: {l.where}.</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
